import { useEffect, useMemo, useState, type ReactNode } from "react";

import { Badge } from "./DesignSystem";

export type UseCaseDetailPanelData = {
  id: string;
  name: string | null;
  problem: string | null;
  outcome: string | null;
  aiOutputs: string | null;
  humanValidation: string | null;
  acceptanceCriteria: string[];
  aiPriorityScore: number | null;
  opportunityScore: number | null;
  impact: number | null;
  feasibility: number | null;
  interUseCaseBenefit: number | null;
  risk: number | null;
  status: string | null;
  estimatedDuration: number | null;
  estimatedCost: number | null;
  plannedStart: string | null;
  plannedEnd: string | null;
  businessScore?: number | null;
  itScore?: number | null;
  complianceScore?: number | null;
  standardScoreSummary?: number | null;
  reviewHistory?: Array<{
    stage?: string;
    role?: string;
    actor?: string | null;
    note?: string | null;
    at?: string;
  }> | null;
  reviewOwner?: string | null;
  reviewStatus: string | null;
  reviewNotes: string | null;
  reviewStage?: string | null;
  reviewStageUpdatedAt?: string | null;
  reviewTransitions?: Array<{ from: string; to: string }>;
  jarvisExportStatus?: string | null;
  jarvisExportError?: string | null;
  jarvisExportedAt?: string | null;
  dependencies?: Array<{ id: string; name: string | null; confidence: number; source: string }>;
  dependents?: Array<{ id: string; name: string | null; confidence: number; source: string }>;
  targetArchitecture?: string | null;
  notes: string | null;
  validation?: {
    role: string;
    requiredFields: { key: string; label: string }[];
    missingFields: string[];
    isComplete: boolean;
  } | null;
  auditEvents?: Array<{
    id: string;
    action: string;
    summary: string;
    createdAt: string;
    actorEmail?: string | null;
    actorRole?: string | null;
    metadata?: Record<string, unknown> | null;
  }> | null;
  rawFields?: Record<string, unknown> | null;
};

type UseCaseEstimatePayload = {
  estimatedDuration: number | null;
  estimatedCost: number | null;
  plannedStart: string | null;
  plannedEnd: string | null;
};

type CardTone = "imported" | "added" | "ai";

function toneClasses(tone: CardTone): string {
  switch (tone) {
    case "added":
      return "border-amber-200/70 bg-amber-50/60";
    case "ai":
      return "border-violet-200/70 bg-violet-50/60";
    default:
      return "border-sky-200/70 bg-sky-50/60";
  }
}

function DetailCard({
  label,
  value,
  tone = "imported",
  children,
}: {
  label: string;
  value?: string | null;
  tone?: CardTone;
  children?: ReactNode;
}): JSX.Element {
  return (
    <div className={`rounded-lg border p-3 ${toneClasses(tone)}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm text-foreground">{children ?? (value || "—")}</div>
    </div>
  );
}

function EditableCard({
  label,
  tone = "added",
  children,
}: {
  label: string;
  tone?: CardTone;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={`rounded-lg border p-3 ${toneClasses(tone)}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-2 text-sm text-foreground">{children}</div>
    </div>
  );
}

function formatScoreValue(value: number | null | undefined): string | null {
  if (value == null) return null;
  if (!Number.isFinite(value)) return null;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function formatRawValue(value: unknown): string | null {
  if (value == null) return null;
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (Array.isArray(value)) {
    const entries = value
      .map((entry) => formatRawValue(entry))
      .filter((entry): entry is string => Boolean(entry));
    return entries.length ? entries.join(", ") : null;
  }
  if (typeof value === "object") {
    const serialized = JSON.stringify(value);
    return serialized && serialized !== "{}" ? serialized : null;
  }
  const text = String(value).trim();
  return text || null;
}

function formatDateInput(value?: string | null): string {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

function parseOptionalNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

const REVIEW_STAGE_LABELS: Record<string, string> = {
  first_review: "1st review",
  it_review: "IT",
  compliance_review: "Compliance",
  hr_review: "HR",
  finance_review: "Finance",
  management_review: "Management",
  final_review: "Final",
};

function formatReviewStage(stage?: string | null): string | null {
  if (!stage) return null;
  return REVIEW_STAGE_LABELS[stage] ?? stage;
}

function formatConfidence(value: number): string {
  if (!Number.isFinite(value)) return "—";
  return `${Math.round(value * 100)}%`;
}

const JARVIS_EXPORT_STATUSES: Record<string, { label: string; tone: "neutral" | "success" | "warning" }> = {
  success: { label: "Exporté", tone: "success" },
  failed: { label: "Erreur", tone: "warning" },
};

type ReviewHistoryEntry = NonNullable<UseCaseDetailPanelData["reviewHistory"]>[number];

function formatReviewHistoryEntry(entry: ReviewHistoryEntry): string {
  const stage = formatReviewStage(entry.stage) ?? "—";
  const role = entry.role ?? "—";
  const actor = entry.actor ?? "—";
  const note = entry.note ?? "—";
  const at = entry.at ? new Date(entry.at).toLocaleString() : "—";
  return `${stage} • ${role} • ${actor} • ${at} • ${note}`;
}

function formatAuditActor(event: NonNullable<UseCaseDetailPanelData["auditEvents"]>[number]): string {
  const actor = event.actorEmail ?? "—";
  const role = event.actorRole ? event.actorRole.toLowerCase() : null;
  return role ? `${actor} · ${role}` : actor;
}

function compactList(values: string[] | null | undefined): string | null {
  if (!values?.length) return null;
  return values.join(", ");
}

type AttachmentEntry = { url: string; filename: string };

type UseCaseEnrichmentData = {
  status: string;
  stopReason: string;
  updatedAt: string;
  questionsJson: Array<{ key: string; category: string; question: string; rationale: string; required: boolean }>;
  answersJson: Record<string, string>;
};

function extractAttachments(value: unknown): AttachmentEntry[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const record = entry as Record<string, unknown>;
      const urlValue = record.url ?? record.URL;
      if (typeof urlValue !== "string" || !urlValue.trim()) return null;
      const filenameValue = record.filename ?? record.name ?? record.title;
      const filename = typeof filenameValue === "string" && filenameValue.trim() ? filenameValue : "Attachment";
      return { url: urlValue, filename };
    })
    .filter(Boolean) as AttachmentEntry[];
}

function truncateUrl(url: string, maxLength = 48): string {
  if (url.length <= maxLength) return url;
  return `${url.slice(0, Math.max(0, maxLength - 10))}…${url.slice(-8)}`;
}

function renderAttachmentList(attachments: AttachmentEntry[]): JSX.Element {
  return (
    <ul className="space-y-1 text-sm">
      {attachments.map((attachment) => (
        <li key={`${attachment.url}-${attachment.filename}`} className="flex flex-col">
          <span className="font-medium">{attachment.filename}</span>
          <a
            href={attachment.url}
            className="text-xs text-blue-600 underline underline-offset-2"
            target="_blank"
            rel="noreferrer"
          >
            {truncateUrl(attachment.url)}
          </a>
        </li>
      ))}
    </ul>
  );
}

function buildRawEntries(rawFields: Record<string, unknown> | null | undefined) {
  if (!rawFields) return [];
  return Object.entries(rawFields).sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
}

export function UseCaseDetailPanel({
  useCase,
  fields,
  canEditReview = false,
  reviewStatusOptions = [],
  reviewOwnerOptions = [],
  onUpdateReview,
  onUpdateDetails,
  onSaveEstimates,
  onExportToJarvis,
  jarvisExporting = false,
  jarvisExportNotice,
  jarvisExportError,
  enrichment,
  canEditEnrichment = false,
  onUpdateEnrichmentAnswers,
  canViewRaw = false,
}: {
  useCase: UseCaseDetailPanelData;
  fields: Array<{ key: string; label: string; enabled: boolean; order: number }>;
  canEditReview?: boolean;
  reviewStatusOptions?: Array<{ value: string; label: string }>;
  reviewOwnerOptions?: Array<{ value: string; label: string }>;
  onUpdateReview?: (payload: {
    reviewStatus?: string | null;
    reviewNotes?: string | null;
    reviewOwner?: string | null;
  }) => Promise<void>;
  onUpdateDetails?: (payload: {
    name?: string | null;
    status?: string | null;
    problem?: string | null;
    outcome?: string | null;
    aiOutputs?: string | null;
    humanValidation?: string | null;
    acceptanceCriteria?: string[] | null;
    notes?: string | null;
    opportunityScore?: number | null;
    impact?: number | null;
    feasibility?: number | null;
    risk?: number | null;
  }) => Promise<void>;
  onSaveEstimates?: (payload: UseCaseEstimatePayload) => Promise<void>;
  onExportToJarvis?: () => Promise<void>;
  jarvisExporting?: boolean;
  jarvisExportNotice?: string | null;
  jarvisExportError?: string | null;
  enrichment?: UseCaseEnrichmentData | null;
  canEditEnrichment?: boolean;
  onUpdateEnrichmentAnswers?: (answers: Record<string, string>) => Promise<void>;
  canViewRaw?: boolean;
}): JSX.Element {
  const [estimatedDurationInput, setEstimatedDurationInput] = useState("");
  const [estimatedCostInput, setEstimatedCostInput] = useState("");
  const [plannedStartInput, setPlannedStartInput] = useState("");
  const [plannedEndInput, setPlannedEndInput] = useState("");
  const [detailInputs, setDetailInputs] = useState({
    name: useCase.name ?? "",
    status: useCase.status ?? "",
    problem: useCase.problem ?? "",
    outcome: useCase.outcome ?? "",
    aiOutputs: useCase.aiOutputs ?? "",
    humanValidation: useCase.humanValidation ?? "",
    acceptanceCriteria: (useCase.acceptanceCriteria ?? []).join("\n"),
    notes: useCase.notes ?? "",
    opportunityScore: useCase.opportunityScore != null ? String(useCase.opportunityScore) : "",
    impact: useCase.impact != null ? String(useCase.impact) : "",
    feasibility: useCase.feasibility != null ? String(useCase.feasibility) : "",
    risk: useCase.risk != null ? String(useCase.risk) : "",
  });
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "success" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [detailSaveStatus, setDetailSaveStatus] = useState<"idle" | "saving" | "error">("idle");
  const [detailSaveError, setDetailSaveError] = useState<string | null>(null);
  const [reviewStatusInput, setReviewStatusInput] = useState(useCase.reviewStatus ?? "");
  const [reviewOwnerInput, setReviewOwnerInput] = useState(useCase.reviewOwner ?? "");
  const [reviewNotesInput, setReviewNotesInput] = useState(useCase.reviewNotes ?? "");
  const [reviewSaveStatus, setReviewSaveStatus] = useState<"idle" | "saving" | "error">("idle");
  const [reviewSaveError, setReviewSaveError] = useState<string | null>(null);
  const [enrichmentAnswersInput, setEnrichmentAnswersInput] = useState<Record<string, string>>(enrichment?.answersJson ?? {});
  const [enrichmentSaveState, setEnrichmentSaveState] = useState<"idle" | "saving" | "error">("idle");

  useEffect(() => {
    setEstimatedDurationInput(useCase.estimatedDuration != null ? String(useCase.estimatedDuration) : "");
    setEstimatedCostInput(useCase.estimatedCost != null ? String(useCase.estimatedCost) : "");
    setPlannedStartInput(formatDateInput(useCase.plannedStart));
    setPlannedEndInput(formatDateInput(useCase.plannedEnd));
    setDetailInputs({
      name: useCase.name ?? "",
      status: useCase.status ?? "",
      problem: useCase.problem ?? "",
      outcome: useCase.outcome ?? "",
      aiOutputs: useCase.aiOutputs ?? "",
      humanValidation: useCase.humanValidation ?? "",
      acceptanceCriteria: (useCase.acceptanceCriteria ?? []).join("\n"),
      notes: useCase.notes ?? "",
      opportunityScore: useCase.opportunityScore != null ? String(useCase.opportunityScore) : "",
      impact: useCase.impact != null ? String(useCase.impact) : "",
      feasibility: useCase.feasibility != null ? String(useCase.feasibility) : "",
      risk: useCase.risk != null ? String(useCase.risk) : "",
    });
    setSaveStatus("idle");
    setSaveError(null);
    setDetailSaveStatus("idle");
    setDetailSaveError(null);
    setReviewStatusInput(useCase.reviewStatus ?? "");
    setReviewOwnerInput(useCase.reviewOwner ?? "");
    setReviewNotesInput(useCase.reviewNotes ?? "");
    setReviewSaveStatus("idle");
    setReviewSaveError(null);
    setEnrichmentAnswersInput(enrichment?.answersJson ?? {});
    setEnrichmentSaveState("idle");
  }, [useCase, enrichment]);

  const isDirty = useMemo(() => {
    const baselineDuration = useCase.estimatedDuration != null ? String(useCase.estimatedDuration) : "";
    const baselineCost = useCase.estimatedCost != null ? String(useCase.estimatedCost) : "";
    const baselineStart = formatDateInput(useCase.plannedStart);
    const baselineEnd = formatDateInput(useCase.plannedEnd);
    return (
      estimatedDurationInput !== baselineDuration ||
      estimatedCostInput !== baselineCost ||
      plannedStartInput !== baselineStart ||
      plannedEndInput !== baselineEnd
    );
  }, [
    estimatedDurationInput,
    estimatedCostInput,
    plannedStartInput,
    plannedEndInput,
    useCase.estimatedDuration,
    useCase.estimatedCost,
    useCase.plannedStart,
    useCase.plannedEnd,
  ]);

  const handleSave = async () => {
    if (!onSaveEstimates) return;
    setSaveError(null);

    const estimatedDuration = parseOptionalNumber(estimatedDurationInput);
    if (estimatedDurationInput.trim() && estimatedDuration == null) {
      setSaveStatus("error");
      setSaveError("La durée estimée doit être un nombre valide.");
      return;
    }
    const estimatedCost = parseOptionalNumber(estimatedCostInput);
    if (estimatedCostInput.trim() && estimatedCost == null) {
      setSaveStatus("error");
      setSaveError("Le coût estimé doit être un nombre valide.");
      return;
    }

    const payload: UseCaseEstimatePayload = {
      estimatedDuration,
      estimatedCost,
      plannedStart: plannedStartInput || null,
      plannedEnd: plannedEndInput || null,
    };

    setSaveStatus("saving");
    try {
      await onSaveEstimates(payload);
      setSaveStatus("success");
    } catch (error) {
      setSaveStatus("error");
      setSaveError(error instanceof Error ? error.message : "Erreur lors de la mise à jour.");
    }
  };

  const resetSaveState = () => {
    if (saveStatus !== "idle") {
      setSaveStatus("idle");
      setSaveError(null);
    }
  };

  const reviewStatusLabel = useMemo(() => {
    const match = reviewStatusOptions.find((option) => option.value === reviewStatusInput);
    return match?.label ?? reviewStatusInput;
  }, [reviewStatusInput, reviewStatusOptions]);

  const handleReviewStatusChange = async (nextValue: string) => {
    setReviewStatusInput(nextValue);
    if (!onUpdateReview) return;
    setReviewSaveStatus("saving");
    try {
      await onUpdateReview({ reviewStatus: nextValue || null });
      setReviewSaveError(null);
      setReviewSaveStatus("idle");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erreur lors de la mise à jour.";
      setReviewSaveError(message);
      setReviewSaveStatus("error");
    }
  };

  const handleReviewOwnerChange = async (nextValue: string) => {
    setReviewOwnerInput(nextValue);
    if (!onUpdateReview) return;
    setReviewSaveStatus("saving");
    try {
      await onUpdateReview({ reviewOwner: nextValue || null });
      setReviewSaveError(null);
      setReviewSaveStatus("idle");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erreur lors de la mise à jour.";
      setReviewSaveError(message);
      setReviewSaveStatus("error");
    }
  };

  const handleReviewNotesBlur = async () => {
    if (!onUpdateReview) return;
    const trimmed = reviewNotesInput.trim();
    if (trimmed === (useCase.reviewNotes ?? "")) return;
    setReviewSaveStatus("saving");
    try {
      await onUpdateReview({ reviewNotes: trimmed || null });
      setReviewSaveError(null);
      setReviewSaveStatus("idle");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erreur lors de la mise à jour.";
      setReviewSaveError(message);
      setReviewSaveStatus("error");
    }
  };

  const handleDetailsBlur = async (
    key: keyof typeof detailInputs,
    nextValue: string,
    baseline: string,
  ) => {
    if (!onUpdateDetails) return;
    if (nextValue.trim() === baseline.trim()) return;
    try {
      const payload: Record<string, unknown> = {};
      if (key === "acceptanceCriteria") {
        const trimmed = nextValue.trim();
        payload[key] = trimmed
          ? trimmed
              .split(/\r?\n|,|;/g)
              .map((entry) => entry.trim())
              .filter(Boolean)
          : null;
      } else if (key === "opportunityScore" || key === "impact" || key === "feasibility" || key === "risk") {
        const parsed = parseOptionalNumber(nextValue);
        if (nextValue.trim() && parsed == null) {
          setDetailSaveError("Les scores doivent être des nombres valides.");
          setDetailSaveStatus("error");
          return;
        }
        payload[key] = parsed;
      } else {
        payload[key] = nextValue.trim() || null;
      }
      setDetailSaveStatus("saving");
      await onUpdateDetails(payload);
      setDetailSaveError(null);
      setDetailSaveStatus("idle");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Erreur lors de la mise à jour.";
      setDetailSaveError(message);
      setDetailSaveStatus("error");
    }
  };

  const validation = useCase.validation ?? null;
  const missingFields = new Set(validation?.missingFields ?? []);
  const dependencies = useCase.dependencies ?? [];
  const dependents = useCase.dependents ?? [];
  const isFinalReview = useCase.reviewStage === "final_review";
  const jarvisStatus = useCase.jarvisExportStatus ? JARVIS_EXPORT_STATUSES[useCase.jarvisExportStatus] : null;
  const auditEvents = useCase.auditEvents ?? [];
  const fieldDefinitions: Record<
    string,
    { label: string; value: string | null; tone: CardTone; editable?: boolean; render?: JSX.Element }
  > = {
    name: {
      label: "Title",
      value: formatRawValue(useCase.name),
      tone: "added",
      editable: Boolean(onUpdateDetails),
      render: onUpdateDetails ? (
        <input
          className="h-9 w-full rounded border border-slate-200 px-2 text-sm"
          value={detailInputs.name}
          onChange={(event) => setDetailInputs((current) => ({ ...current, name: event.target.value }))}
          onBlur={() => void handleDetailsBlur("name", detailInputs.name, useCase.name ?? "")}
          disabled={detailSaveStatus === "saving"}
        />
      ) : undefined,
    },
    status: {
      label: "Status",
      value: formatRawValue(useCase.status),
      tone: "added",
      editable: Boolean(onUpdateDetails),
      render: onUpdateDetails ? (
        <input
          className="h-9 w-full rounded border border-slate-200 px-2 text-sm"
          value={detailInputs.status}
          onChange={(event) => setDetailInputs((current) => ({ ...current, status: event.target.value }))}
          onBlur={() => void handleDetailsBlur("status", detailInputs.status, useCase.status ?? "")}
          disabled={detailSaveStatus === "saving"}
        />
      ) : undefined,
    },
    reviewStatus: {
      label: "Review status",
      value: formatRawValue(reviewStatusLabel),
      tone: "added",
      editable: canEditReview,
      render: canEditReview ? (
        <select
          className="h-9 w-full rounded border border-slate-200 bg-white px-2 text-sm"
          value={reviewStatusInput}
          onChange={(event) => void handleReviewStatusChange(event.target.value)}
          disabled={reviewSaveStatus === "saving"}
        >
          <option value="">Sélectionner…</option>
          {reviewStatusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : undefined,
    },
    reviewOwner: {
      label: "Owner",
      value: formatRawValue(useCase.reviewOwner),
      tone: "added",
      editable: canEditReview,
      render: canEditReview ? (
        <select
          className="h-9 w-full rounded border border-slate-200 bg-white px-2 text-sm"
          value={reviewOwnerInput}
          onChange={(event) => void handleReviewOwnerChange(event.target.value)}
          disabled={reviewSaveStatus === "saving"}
        >
          <option value="">Sélectionner…</option>
          {reviewOwnerOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : undefined,
    },
    reviewNotes: {
      label: "Review notes",
      value: formatRawValue(useCase.reviewNotes),
      tone: "ai",
      editable: canEditReview,
      render: canEditReview ? (
        <textarea
          className="min-h-[96px] w-full rounded border border-slate-200 px-2 py-1 text-sm"
          value={reviewNotesInput}
          onChange={(event) => setReviewNotesInput(event.target.value)}
          onBlur={() => void handleReviewNotesBlur()}
          disabled={reviewSaveStatus === "saving"}
        />
      ) : undefined,
    },
    problem: {
      label: "Problem",
      value: formatRawValue(useCase.problem),
      tone: "added",
      editable: Boolean(onUpdateDetails),
      render: onUpdateDetails ? (
        <textarea
          className="min-h-[96px] w-full rounded border border-slate-200 px-2 py-1 text-sm"
          value={detailInputs.problem}
          onChange={(event) => setDetailInputs((current) => ({ ...current, problem: event.target.value }))}
          onBlur={() => void handleDetailsBlur("problem", detailInputs.problem, useCase.problem ?? "")}
          disabled={detailSaveStatus === "saving"}
        />
      ) : undefined,
    },
    outcome: {
      label: "Outcome",
      value: formatRawValue(useCase.outcome),
      tone: "added",
      editable: Boolean(onUpdateDetails),
      render: onUpdateDetails ? (
        <textarea
          className="min-h-[96px] w-full rounded border border-slate-200 px-2 py-1 text-sm"
          value={detailInputs.outcome}
          onChange={(event) => setDetailInputs((current) => ({ ...current, outcome: event.target.value }))}
          onBlur={() => void handleDetailsBlur("outcome", detailInputs.outcome, useCase.outcome ?? "")}
          disabled={detailSaveStatus === "saving"}
        />
      ) : undefined,
    },
    aiOutputs: {
      label: "AI outputs",
      value: formatRawValue(useCase.aiOutputs),
      tone: "ai",
      editable: Boolean(onUpdateDetails),
      render: onUpdateDetails ? (
        <textarea
          className="min-h-[96px] w-full rounded border border-slate-200 px-2 py-1 text-sm"
          value={detailInputs.aiOutputs}
          onChange={(event) => setDetailInputs((current) => ({ ...current, aiOutputs: event.target.value }))}
          onBlur={() => void handleDetailsBlur("aiOutputs", detailInputs.aiOutputs, useCase.aiOutputs ?? "")}
          disabled={detailSaveStatus === "saving"}
        />
      ) : undefined,
    },
    humanValidation: {
      label: "Human validation",
      value: formatRawValue(useCase.humanValidation),
      tone: "ai",
      editable: Boolean(onUpdateDetails),
      render: onUpdateDetails ? (
        <textarea
          className="min-h-[96px] w-full rounded border border-slate-200 px-2 py-1 text-sm"
          value={detailInputs.humanValidation}
          onChange={(event) => setDetailInputs((current) => ({ ...current, humanValidation: event.target.value }))}
          onBlur={() => void handleDetailsBlur("humanValidation", detailInputs.humanValidation, useCase.humanValidation ?? "")}
          disabled={detailSaveStatus === "saving"}
        />
      ) : undefined,
    },
    acceptanceCriteria: {
      label: "Acceptance criteria",
      value: compactList(useCase.acceptanceCriteria),
      tone: "ai",
      editable: Boolean(onUpdateDetails),
      render: onUpdateDetails ? (
        <textarea
          className="min-h-[96px] w-full rounded border border-slate-200 px-2 py-1 text-sm"
          value={detailInputs.acceptanceCriteria}
          onChange={(event) =>
            setDetailInputs((current) => ({ ...current, acceptanceCriteria: event.target.value }))
          }
          onBlur={() =>
            void handleDetailsBlur(
              "acceptanceCriteria",
              detailInputs.acceptanceCriteria,
              (useCase.acceptanceCriteria ?? []).join("\n"),
            )
          }
          disabled={detailSaveStatus === "saving"}
        />
      ) : undefined,
    },
    notes: {
      label: "Notes",
      value: formatRawValue(useCase.notes),
      tone: "ai",
      editable: Boolean(onUpdateDetails),
      render: onUpdateDetails ? (
        <textarea
          className="min-h-[96px] w-full rounded border border-slate-200 px-2 py-1 text-sm"
          value={detailInputs.notes}
          onChange={(event) => setDetailInputs((current) => ({ ...current, notes: event.target.value }))}
          onBlur={() => void handleDetailsBlur("notes", detailInputs.notes, useCase.notes ?? "")}
          disabled={detailSaveStatus === "saving"}
        />
      ) : undefined,
    },
    aiPriorityScore: { label: "AI priority score", value: formatScoreValue(useCase.aiPriorityScore), tone: "added" },
    opportunityScore: {
      label: "Opportunity score",
      value: formatScoreValue(useCase.opportunityScore),
      tone: "added",
      editable: Boolean(onUpdateDetails),
      render: onUpdateDetails ? (
        <input
          className="h-9 w-full rounded border border-slate-200 px-2 text-sm"
          type="number"
          value={detailInputs.opportunityScore}
          onChange={(event) =>
            setDetailInputs((current) => ({ ...current, opportunityScore: event.target.value }))
          }
          onBlur={() =>
            void handleDetailsBlur(
              "opportunityScore",
              detailInputs.opportunityScore,
              useCase.opportunityScore != null ? String(useCase.opportunityScore) : "",
            )
          }
          disabled={detailSaveStatus === "saving"}
        />
      ) : undefined,
    },
    impact: {
      label: "Impact",
      value: formatScoreValue(useCase.impact),
      tone: "added",
      editable: Boolean(onUpdateDetails),
      render: onUpdateDetails ? (
        <input
          className="h-9 w-full rounded border border-slate-200 px-2 text-sm"
          type="number"
          value={detailInputs.impact}
          onChange={(event) => setDetailInputs((current) => ({ ...current, impact: event.target.value }))}
          onBlur={() =>
            void handleDetailsBlur(
              "impact",
              detailInputs.impact,
              useCase.impact != null ? String(useCase.impact) : "",
            )
          }
          disabled={detailSaveStatus === "saving"}
        />
      ) : undefined,
    },
    feasibility: {
      label: "Feasibility",
      value: formatScoreValue(useCase.feasibility),
      tone: "added",
      editable: Boolean(onUpdateDetails),
      render: onUpdateDetails ? (
        <input
          className="h-9 w-full rounded border border-slate-200 px-2 text-sm"
          type="number"
          value={detailInputs.feasibility}
          onChange={(event) => setDetailInputs((current) => ({ ...current, feasibility: event.target.value }))}
          onBlur={() =>
            void handleDetailsBlur(
              "feasibility",
              detailInputs.feasibility,
              useCase.feasibility != null ? String(useCase.feasibility) : "",
            )
          }
          disabled={detailSaveStatus === "saving"}
        />
      ) : undefined,
    },
    risk: {
      label: "Risk",
      value: formatScoreValue(useCase.risk),
      tone: "added",
      editable: Boolean(onUpdateDetails),
      render: onUpdateDetails ? (
        <input
          className="h-9 w-full rounded border border-slate-200 px-2 text-sm"
          type="number"
          value={detailInputs.risk}
          onChange={(event) => setDetailInputs((current) => ({ ...current, risk: event.target.value }))}
          onBlur={() =>
            void handleDetailsBlur(
              "risk",
              detailInputs.risk,
              useCase.risk != null ? String(useCase.risk) : "",
            )
          }
          disabled={detailSaveStatus === "saving"}
        />
      ) : undefined,
    },
    estimatedDuration: {
      label: "Estimated duration",
      value: useCase.estimatedDuration != null ? String(useCase.estimatedDuration) : null,
      tone: "added",
    },
    estimatedCost: {
      label: "Estimated cost",
      value: useCase.estimatedCost != null ? String(useCase.estimatedCost) : null,
      tone: "added",
    },
    plannedStart: { label: "Planned start", value: formatDateInput(useCase.plannedStart), tone: "added" },
    plannedEnd: { label: "Planned end", value: formatDateInput(useCase.plannedEnd), tone: "added" },
    targetArchitecture: { label: "Target architecture", value: formatRawValue(useCase.targetArchitecture), tone: "added" },
    reviewStage: { label: "Review stage", value: formatReviewStage(useCase.reviewStage), tone: "added" },
  };

  const orderedFields = useMemo(() => [...fields].sort((a, b) => a.order - b.order), [fields]);

  const cards = orderedFields
    .filter((field) => field.enabled)
    .map((field) => {
      if (field.key.startsWith("raw:")) {
        const rawKey = field.key.slice(4);
        const rawValue = useCase.rawFields ? useCase.rawFields[rawKey] : null;
        const attachments = extractAttachments(rawValue);
        const value = formatRawValue(rawValue);
        const shouldRender = attachments.length > 0 || Boolean(value);
        if (!shouldRender) return null;
        return (
          <DetailCard key={field.key} label={field.label} tone="imported">
            {attachments.length ? renderAttachmentList(attachments) : value}
          </DetailCard>
        );
      }

      const card = fieldDefinitions[field.key];
      if (!card) return null;
      const isEditable = Boolean(card.editable && card.render);
      const hasValue = Boolean(card.value && card.value.trim());
      if (!isEditable && !hasValue) return null;
      if (isEditable && card.render) {
        return (
          <EditableCard key={field.key} label={field.label} tone={card.tone}>
            {card.render}
          </EditableCard>
        );
      }
      return <DetailCard key={field.key} label={field.label} value={card.value} tone={card.tone} />;
    })
    .filter(Boolean);

  const rawEntries = useMemo(() => buildRawEntries(useCase.rawFields), [useCase.rawFields]);

  return (
    <div className="space-y-4">
      {cards.length ? (
        <div className="grid gap-3 md:grid-cols-2">{cards}</div>
      ) : (
        <div className="text-sm text-slate-500">Aucune donnée à afficher.</div>
      )}
      {reviewSaveError ? <div className="text-xs text-rose-600">{reviewSaveError}</div> : null}
      {detailSaveError ? <div className="text-xs text-rose-600">{detailSaveError}</div> : null}

      {validation?.requiredFields?.length ? (
        <div className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Required fields ({validation.role.toLowerCase()})
          </div>
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <div className="space-y-2 text-sm">
              {validation.requiredFields.map((field) => {
                const isMissing = missingFields.has(field.key);
                return (
                  <div key={field.key} className="flex items-center justify-between gap-2">
                    <span className={isMissing ? "text-red-600" : "text-foreground"}>{field.label}</span>
                    <span className={isMissing ? "text-xs text-red-600" : "text-xs text-emerald-600"}>
                      {isMissing ? "Missing" : "OK"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}

      {(dependencies.length || dependents.length) && (
        <div className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Inter-use case dependencies
          </div>
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <div className="mt-2 space-y-2 text-sm text-foreground">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Depends on</div>
                {dependencies.length ? (
                  <ul className="mt-1 space-y-1">
                    {dependencies.map((dependency) => (
                      <li key={`${dependency.id}-${dependency.source}`} className="flex items-center gap-2">
                        <span>{dependency.name ?? dependency.id}</span>
                        <span className="text-xs text-muted-foreground">
                          ({formatConfidence(dependency.confidence)} · {dependency.source})
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-muted-foreground">Aucune dépendance détectée.</div>
                )}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Unlocks</div>
                {dependents.length ? (
                  <ul className="mt-1 space-y-1">
                    {dependents.map((dependent) => (
                      <li key={`${dependent.id}-${dependent.source}`} className="flex items-center gap-2">
                        <span>{dependent.name ?? dependent.id}</span>
                        <span className="text-xs text-muted-foreground">
                          ({formatConfidence(dependent.confidence)} · {dependent.source})
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-muted-foreground">Aucun cas aval détecté.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Planning & estimates</div>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Estimated duration
            <input
              type="number"
              className="input text-sm font-normal normal-case"
              value={estimatedDurationInput}
              onChange={(event) => {
                setEstimatedDurationInput(event.target.value);
                resetSaveState();
              }}
              disabled={!onSaveEstimates || saveStatus === "saving"}
              placeholder="Days"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Estimated cost
            <input
              type="number"
              className="input text-sm font-normal normal-case"
              value={estimatedCostInput}
              onChange={(event) => {
                setEstimatedCostInput(event.target.value);
                resetSaveState();
              }}
              disabled={!onSaveEstimates || saveStatus === "saving"}
              placeholder="Budget"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Planned start
            <input
              type="date"
              className="input text-sm font-normal normal-case"
              value={plannedStartInput}
              onChange={(event) => {
                setPlannedStartInput(event.target.value);
                resetSaveState();
              }}
              disabled={!onSaveEstimates || saveStatus === "saving"}
            />
          </label>
          <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Planned end
            <input
              type="date"
              className="input text-sm font-normal normal-case"
              value={plannedEndInput}
              onChange={(event) => {
                setPlannedEndInput(event.target.value);
                resetSaveState();
              }}
              disabled={!onSaveEstimates || saveStatus === "saving"}
            />
          </label>
        </div>
        {onSaveEstimates ? (
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <button
              type="button"
              className="button button-primary"
              onClick={handleSave}
              disabled={!isDirty || saveStatus === "saving"}
            >
              {saveStatus === "saving" ? "Enregistrement..." : "Enregistrer"}
            </button>
            {saveStatus === "success" ? <span className="text-xs text-emerald-600">Enregistré.</span> : null}
            {saveStatus === "error" && saveError ? (
              <span className="text-xs text-red-600">{saveError}</span>
            ) : null}
          </div>
        ) : null}
      </div>

      {(() => {
        const scoreCards = [
          { label: "Business score", value: formatScoreValue(useCase.businessScore), tone: "added" as const },
          { label: "IT score", value: formatScoreValue(useCase.itScore), tone: "added" as const },
          { label: "Compliance score", value: formatScoreValue(useCase.complianceScore), tone: "added" as const },
          { label: "Score summary", value: formatScoreValue(useCase.standardScoreSummary), tone: "added" as const },
        ].filter((card) => card.value);
        if (!scoreCards.length) return null;
        return (
          <div className="space-y-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Standard scoring</div>
            <div className="grid gap-3 md:grid-cols-2">
              {scoreCards.map((card) => (
                <DetailCard key={card.label} label={card.label} value={card.value} tone={card.tone} />
              ))}
            </div>
          </div>
        );
      })()}

      {useCase.reviewTransitions?.length ? (
        <div className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Allowed transitions
          </div>
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <ul className="mt-2 space-y-1 text-sm text-foreground">
              {useCase.reviewTransitions.map((transition) => (
                <li key={`${transition.from}-${transition.to}`}>
                  {formatReviewStage(transition.from)} → {formatReviewStage(transition.to)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
      {useCase.reviewHistory?.length ? (
        <div className="space-y-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Approval history</div>
          <div className="rounded-lg border border-border bg-muted/40 p-3">
            <ul className="mt-2 space-y-1 text-sm text-foreground">
              {useCase.reviewHistory.map((entry, index) => (
                <li key={`${entry.stage ?? "stage"}-${index}`}>{formatReviewHistoryEntry(entry)}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Timeline</div>
        <div className="rounded-lg border border-border bg-muted/40 p-3">
          {auditEvents.length ? (
            <ul className="space-y-3 text-sm text-foreground">
              {auditEvents.map((event) => (
                <li key={event.id} className="space-y-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium">{event.summary}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(event.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground">{formatAuditActor(event)}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-muted-foreground">Aucun événement enregistré.</div>
          )}
        </div>
      </div>

      {enrichment ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Enrichment (1st review)</div>
            <div className="text-xs text-muted-foreground">{enrichment.status} · {new Date(enrichment.updatedAt).toLocaleString()}</div>
          </div>
          <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-3">
            {(enrichment.questionsJson ?? []).map((question) => (
              <div key={question.key} className="space-y-1">
                <div className="text-sm font-medium text-foreground">{question.key} · {question.question}</div>
                <div className="text-xs text-muted-foreground">{question.category} · {question.rationale}</div>
                <textarea
                  className="textarea textarea-bordered w-full min-h-[72px]"
                  value={enrichmentAnswersInput[question.key] ?? ""}
                  onChange={(event) => setEnrichmentAnswersInput((prev) => ({ ...prev, [question.key]: event.target.value }))}
                  readOnly={!canEditEnrichment}
                />
              </div>
            ))}
            {canEditEnrichment && onUpdateEnrichmentAnswers ? (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="button button-secondary"
                  disabled={enrichmentSaveState === "saving"}
                  onClick={async () => {
                    setEnrichmentSaveState("saving");
                    try {
                      await onUpdateEnrichmentAnswers(enrichmentAnswersInput);
                      setEnrichmentSaveState("idle");
                    } catch {
                      setEnrichmentSaveState("error");
                    }
                  }}
                >
                  {enrichmentSaveState === "saving" ? "Saving..." : "Save enrichment answers"}
                </button>
                {enrichmentSaveState === "error" ? <span className="text-xs text-red-600">Save failed</span> : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="space-y-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Jarvis</div>
        <div className="rounded-lg border border-border bg-muted/40 p-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">Basculer vers Jarvis</div>
              {isFinalReview ? (
                <div className="text-xs text-muted-foreground">Export prêt pour Jarvis.</div>
              ) : (
                <div className="text-xs text-muted-foreground">Disponible après validation finale.</div>
              )}
            </div>
            {isFinalReview ? (
              <button
                type="button"
                className="button button-primary"
                onClick={() => onExportToJarvis?.()}
                disabled={!onExportToJarvis || jarvisExporting}
              >
                {jarvisExporting ? "Export..." : "Basculer vers Jarvis"}
              </button>
            ) : null}
          </div>
          {jarvisStatus ? (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <Badge label={jarvisStatus.label} tone={jarvisStatus.tone} />
              {useCase.jarvisExportedAt ? (
                <span className="text-muted-foreground">
                  Dernier export: {new Date(useCase.jarvisExportedAt).toLocaleString()}
                </span>
              ) : null}
            </div>
          ) : null}
          {jarvisExportNotice ? <div className="mt-2 text-xs text-emerald-600">{jarvisExportNotice}</div> : null}
          {jarvisExportError ? <div className="mt-2 text-xs text-red-600">{jarvisExportError}</div> : null}
          {!jarvisExportError && useCase.jarvisExportError ? (
            <div className="mt-2 text-xs text-red-600">{useCase.jarvisExportError}</div>
          ) : null}
        </div>
      </div>

      {canViewRaw && rawEntries.length ? (
        <details className="rounded-lg border border-border bg-muted/30 p-3">
          <summary className="cursor-pointer text-sm font-semibold text-foreground">Raw data</summary>
          <div className="mt-3 space-y-2">
            {rawEntries.map(([key, value]) => {
              const attachments = extractAttachments(value);
              const formatted = formatRawValue(value);
              if (!attachments.length && !formatted) return null;
              return (
                <div key={key} className="rounded-md border border-border/60 bg-white p-2 text-sm">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{key}</div>
                  <div className="mt-1 text-foreground">
                    {attachments.length ? renderAttachmentList(attachments) : formatted}
                  </div>
                </div>
              );
            })}
          </div>
        </details>
      ) : null}

    </div>
  );
}
